// public/js/base/baseLogger.js

/**
 * Centralized Logging System
 * Provides global and per-module logging control with zero performance impact when disabled
 */

const LogConfig = {
    enabled: true,
    showInitMessages: true,
    levels: ['error', 'warn', 'info'],
    showTimestamps: true,
    showModuleNames: true,
    
    modules: {
        'BaseObject': { enabled: true, levels: ['error', 'warn'], color: '#888888' },
        'BaseManager': { enabled: true, levels: ['error', 'warn', 'info'], color: '#4CAF50' },
        'BaseController': { enabled: true, levels: ['error', 'warn', 'info'], color: '#2196F3' },
        'BaseViews': { enabled: true, levels: ['error', 'warn'], color: '#FF9800' },
        
        'DogObject': { enabled: true, levels: ['error', 'warn'], color: '#8B4513' },
        'DogManager': { enabled: true, levels: ['error', 'warn', 'info', 'debug'], color: '#4CAF50' },
        'DogController': { enabled: true, levels: ['error', 'warn', 'info'], color: '#2196F3' },
        'DogViews': { enabled: true, levels: ['error', 'warn'], color: '#FF9800' },
        
        'ActivityObject': { enabled: true, levels: ['error', 'warn'] },
        'ActivityManager': { enabled: true, levels: ['error', 'warn', 'info'] },
        'ActivityController': { enabled: true, levels: ['error', 'warn', 'info'] },
        'ActivityViews': { enabled: true, levels: ['error', 'warn'] },
        
        'FileObject': { enabled: true, levels: ['error', 'warn'] },
        'FileManager': { enabled: true, levels: ['error', 'warn', 'info'] },
        'FileController': { enabled: true, levels: ['error', 'warn', 'info', 'debug'] },
        'FileViews': { enabled: true, levels: ['error', 'warn'] },

        'AuthController': { enabled: true, levels: ['error', 'warn', 'info'], color: '#9C27B0' },
        'TokenManager': { enabled: true, levels: ['error', 'warn', 'info'], color: '#9C27B0' },

        'ParcourToolbar': { enabled: true, levels: ['error', 'warn', 'info'], color: '#FF5722' },
        'ParcourEncoder': { enabled: true, levels: ['error', 'warn'], color: '#673AB7' },
        'ParcourSecureExporter': { enabled: true, levels: ['error', 'warn', 'info'], color: '#00BCD4' },
        'ParcourSecureImporter': { enabled: true, levels: ['error', 'warn', 'info'], color: '#009688' },

        'AppNav': { enabled: true, levels: ['error', 'warn', 'info'], color: '#3F51B5' },
        'FloatingMenuController': { enabled: true, levels: ['error', 'warn'], color: '#607D8B' },
        'MobileDrawerController': { enabled: true, levels: ['error', 'warn'], color: '#795548' },
        'AuthBridge': { enabled: true, levels: ['error', 'warn', 'info'], color: '#E91E63' },

        'Router': { enabled: true, levels: ['error', 'warn', 'info'] },
        'API': { enabled: true, levels: ['error', 'warn', 'info', 'debug'] },
        'ContactFormController': { enabled: true, levels: ['error', 'warn'] },
        'Overlay': { enabled: false, levels: ['error', 'warn'] }
    },
    
    environments: {
        development: {
            enabled: true,
            levels: ['error', 'warn', 'info', 'debug'],
            showTimestamps: true
        },
        production: {
            enabled: false,
            levels: ['error']
        },
        testing: {
            enabled: true,
            levels: ['error', 'warn'],
            showTimestamps: false
        }
    }
};

class BaseLogger {
    constructor(moduleName) {
        this.moduleName = moduleName;
        this.moduleConfig = LogConfig.modules[moduleName] || {};
        
        this.logCache = {
            debug: null,
            info: null,
            warn: null,
            error: null
        };
        
        this.configVersion = 0;

        // LogController.setGlobalEnabled(false);
    }

    /**
     * Check if logging should occur for this level
     * @param {string} level - Log level (debug, info, warn, error)
     * @returns {boolean} True if should log
     */
    shouldLog(level) {
        if (this.logCache[level] !== null && this.configVersion === LogConfig._version) {
            return this.logCache[level];
        }

        let result = false;

        if (!LogConfig.enabled) {
            result = false;
        } else {
            const moduleEnabled = this.moduleConfig.enabled !== false;
            
            if (moduleEnabled) {
                const allowedLevels = this.moduleConfig.levels || LogConfig.levels;
                result = allowedLevels.includes(level);
            } else {
                result = false;
            }
        }

        this.logCache[level] = result;
        this.configVersion = LogConfig._version || 0;

        
        return result;
    }

    /**
     * Format log message with timestamp and module name
     * @param {string} level - Log level
     * @param {string} emoji - Emoji for this log type
     * @param {Array} args - Arguments to log
     * @returns {Array} Formatted arguments
     */
    formatMessage(level, emoji, args) {
        const parts = [];
        
        if (LogConfig.showTimestamps) {
            const timestamp = new Date().toISOString().slice(11, 23);
            parts.push(`[${timestamp}]`);
        }
        
        if (LogConfig.showModuleNames) {
            parts.push(`${emoji} [${this.moduleName}]`);
        } else {
            parts.push(emoji);
        }
        
        parts.push(...args);
        
        return parts;
    }

    /**
     * Apply color styling if supported
     * @param {Array} args - Formatted arguments
     * @returns {Array} Styled arguments
     */
    applyColorStyling(args) {
        const moduleColor = this.moduleConfig.color;
        
        if (typeof window !== 'undefined' && moduleColor && args.length > 0) {
            const styledArgs = [...args];
            if (styledArgs[0] && typeof styledArgs[0] === 'string' && styledArgs[0].includes(`[${this.moduleName}]`)) {
                styledArgs[0] = styledArgs[0].replace(
                    `[${this.moduleName}]`,
                    `%c[${this.moduleName}]%c`
                );
                styledArgs.splice(1, 0, `color: ${moduleColor}; font-weight: bold`, 'color: inherit');
            }
            return styledArgs;
        }
        
        return args;
    }

    /**
     * Debug level logging - detailed information for debugging
     */
    debug(...args) {
        if (this.shouldLog('debug')) {
            const formatted = this.formatMessage('debug', '🔍', args);
            const styled = this.applyColorStyling(formatted);
            console.log(...styled);
        }
    }

    /**
     * Info level logging - general information
     */
    info(...args) {
        if (this.shouldLog('info')) {
            const formatted = this.formatMessage('info', '📝', args);
            const styled = this.applyColorStyling(formatted);
            console.log(...styled);
        }
    }

    /**
     * Warning level logging - something unexpected but not critical
     */
    warn(...args) {
        if (this.shouldLog('warn')) {
            const formatted = this.formatMessage('warn', '⚠️', args);
            const styled = this.applyColorStyling(formatted);
            console.warn(...styled);
        }
    }

    /**
     * Error level logging - critical errors
     */
    error(...args) {
        if (this.shouldLog('error')) {
            const formatted = this.formatMessage('error', '❌', args);
            const styled = this.applyColorStyling(formatted);
            console.error(...styled);
        }
    }

    /**
     * Cache-related logging
     */
    cache(...args) {
        if (this.shouldLog('debug')) {
            const formatted = this.formatMessage('debug', '💾', args);
            const styled = this.applyColorStyling(formatted);
            console.log(...styled);
        }
    }

    /**
     * API-related logging
     */
    api(...args) {
        if (this.shouldLog('info')) {
            const formatted = this.formatMessage('info', '🌐', args);
            const styled = this.applyColorStyling(formatted);
            console.log(...styled);
        }
    }

    /**
     * Performance-related logging
     */
    perf(...args) {
        if (this.shouldLog('debug')) {
            const formatted = this.formatMessage('debug', '⚡', args);
            const styled = this.applyColorStyling(formatted);
            console.log(...styled);
        }
    }

    /**
     * Database/storage-related logging
     */
    db(...args) {
        if (this.shouldLog('debug')) {
            const formatted = this.formatMessage('debug', '🗄️', args);
            const styled = this.applyColorStyling(formatted);
            console.log(...styled);
        }
    }

    /**
     * User interface-related logging
     */
    ui(...args) {
        if (this.shouldLog('debug')) {
            const formatted = this.formatMessage('debug', '🎨', args);
            const styled = this.applyColorStyling(formatted);
            console.log(...styled);
        }
    }

    /**
     * Security-related logging
     */
    security(...args) {
        if (this.shouldLog('warn')) {
            const formatted = this.formatMessage('warn', '🔒', args);
            const styled = this.applyColorStyling(formatted);
            console.warn(...styled);
        }
    }

    /**
     * Success/completion logging
     */
    success(...args) {
        if (this.shouldLog('info')) {
            const formatted = this.formatMessage('info', '✅', args);
            const styled = this.applyColorStyling(formatted);
            console.log(...styled);
        }
    }

    /**
     * Process/workflow logging
     */
    process(...args) {
        if (this.shouldLog('info')) {
            const formatted = this.formatMessage('info', '⚙️', args);
            const styled = this.applyColorStyling(formatted);
            console.log(...styled);
        }
    }

    /**
     * Start a performance timer
     * @param {string} label - Timer label
     */
    startTimer(label) {
        if (this.shouldLog('debug')) {
            this.debug(`⏱️ Starting timer: ${label}`);
            console.time(`${this.moduleName}:${label}`);
        }
    }

    /**
     * End a performance timer
     * @param {string} label - Timer label
     */
    endTimer(label) {
        if (this.shouldLog('debug')) {
            console.timeEnd(`${this.moduleName}:${label}`);
            this.debug(`⏱️ Timer ended: ${label}`);
        }
    }

    /**
     * Log execution time of a function
     * @param {string} label - Operation label
     * @param {Function} fn - Function to time
     * @returns {*} Function result
     */
    async timeAsync(label, fn) {
        if (this.shouldLog('debug')) {
            this.startTimer(label);
            try {
                const result = await fn();
                this.endTimer(label);
                return result;
            } catch (error) {
                this.endTimer(label);
                this.error(`Error in timed operation ${label}:`, error);
                throw error;
            }
        } else {
            return await fn();
        }
    }

    /**
     * Start a collapsible log group
     * @param {string} groupName - Group name
     */
    group(groupName) {
        if (this.shouldLog('info')) {
            const formatted = this.formatMessage('info', '📂', [groupName]);
            console.group(...formatted);
        }
    }

    /**
     * Start a collapsed log group
     * @param {string} groupName - Group name
     */
    groupCollapsed(groupName) {
        if (this.shouldLog('info')) {
            const formatted = this.formatMessage('info', '📁', [groupName]);
            console.groupCollapsed(...formatted);
        }
    }

    /**
     * End the current log group
     */
    groupEnd() {
        if (this.shouldLog('info')) {
            console.groupEnd();
        }
    }

    /**
     * Log data as a table
     * @param {*} data - Data to display as table
     * @param {Array} columns - Columns to show (optional)
     */
    table(data, columns) {
        if (this.shouldLog('info')) {
            this.info('Data table:');
            console.table(data, columns);
        }
    }
}

/**
 * Create a logger instance for a specific module
 * @param {string} moduleName - Name of the module (usually class name)
 * @returns {BaseLogger} Logger instance
 */
export function createLogger(moduleName) {
    return new BaseLogger(moduleName);
}

export const LogController = {
    /**
     * Enable logging for a specific module
     * @param {string} moduleName - Module name
     */
    enableModule(moduleName) {
        if (!LogConfig.modules[moduleName]) {
            LogConfig.modules[moduleName] = {};
        }
        LogConfig.modules[moduleName].enabled = true;
        this.invalidateCache();
    },

    /**
     * Disable logging for a specific module
     * @param {string} moduleName - Module name
     */
    disableModule(moduleName) {
        if (!LogConfig.modules[moduleName]) {
            LogConfig.modules[moduleName] = {};
        }
        LogConfig.modules[moduleName].enabled = false;
        this.invalidateCache();
    },

    /**
     * Set log levels for a specific module
     * @param {string} moduleName - Module name
     * @param {Array<string>} levels - Array of levels to enable
     */
    setModuleLevels(moduleName, levels) {
        if (!LogConfig.modules[moduleName]) {
            LogConfig.modules[moduleName] = {};
        }
        LogConfig.modules[moduleName].levels = levels;
        this.invalidateCache();
    },

    /**
     * Enable or disable global logging
     * @param {boolean} enabled - Global logging state
     */
    setGlobalEnabled(enabled) {
        LogConfig.enabled = enabled;
        this.invalidateCache();
    },

    /**
     * Set global log levels
     * @param {Array<string>} levels - Array of levels to enable globally
     */
    setGlobalLevels(levels) {
        LogConfig.levels = levels;
        this.invalidateCache();
    },

    /**
     * Apply environment-specific configuration
     * @param {string} environment - Environment name (development, production, testing)
     */
    setEnvironment(environment) {
        const envConfig = LogConfig.environments[environment];
        if (envConfig) {
            Object.assign(LogConfig, envConfig);
            this.invalidateCache();
        }
    },

    /**
     * Enable timestamps in log messages
     * @param {boolean} enabled - Whether to show timestamps
     */
    setTimestamps(enabled) {
        LogConfig.showTimestamps = enabled;
    },

    /**
     * Enable/disable module names in log messages
     * @param {boolean} enabled - Whether to show module names
     */
    setModuleNames(enabled) {
        LogConfig.showModuleNames = enabled;
    },

    /**
     * Get current logging configuration
     * @returns {Object} Current configuration
     */
    getConfig() {
        return JSON.parse(JSON.stringify(LogConfig));
    },

    /**
     * Get logging status for all modules
     * @returns {Object} Status information
     */
    getStatus() {
        const status = {
            globalEnabled: LogConfig.enabled,
            globalLevels: LogConfig.levels,
            moduleCount: Object.keys(LogConfig.modules).length,
            enabledModules: [],
            disabledModules: []
        };

        Object.entries(LogConfig.modules).forEach(([name, config]) => {
            if (config.enabled !== false) {
                status.enabledModules.push({
                    name,
                    levels: config.levels || LogConfig.levels
                });
            } else {
                status.disabledModules.push(name);
            }
        });

        return status;
    },

    /**
     * Invalidate all logger caches (force recalculation)
     */
    invalidateCache() {
        LogConfig._version = (LogConfig._version || 0) + 1;
    },

    presets: {
        debug() {
            LogController.setGlobalEnabled(true);
            LogController.setGlobalLevels(['error', 'warn', 'info', 'debug']);
            LogController.setTimestamps(true);
        },

        production() {
            LogController.setGlobalEnabled(false);
            LogController.setGlobalLevels(['error']);
            LogController.setTimestamps(false);
        },

        testing() {
            LogController.setGlobalEnabled(true);
            LogController.setGlobalLevels(['error', 'warn']);
            LogController.setTimestamps(false);
        },

        silent() {
            LogController.setGlobalEnabled(false);
        },

        verbose() {
            LogController.setGlobalEnabled(true);
            LogController.setGlobalLevels(['error', 'warn', 'info', 'debug']);
            LogController.setTimestamps(true);

            Object.keys(LogConfig.modules).forEach(moduleName => {
                LogController.enableModule(moduleName);
                LogController.setModuleLevels(moduleName, ['error', 'warn', 'info', 'debug']);
            });
        }
    }
};

if (typeof window !== 'undefined') {
    window.LogController = LogController;
    window.LogConfig = LogConfig;
}

if (typeof window !== 'undefined') {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        LogController.setEnvironment('development');
    } else if (window.location.hostname.includes('test') || window.location.hostname.includes('staging')) {
        LogController.setEnvironment('testing');
    } else {
        LogController.setEnvironment('production');
    }
}

export default { createLogger, LogController };